import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../../shared/services/api.service';
import {
  IPostAnalysisListResponse,
  IPostAnalysisPublication,
  ISequenceMetadataCreate,
} from '../models/SequenceMetadata';

/**
 * Client for the post-analysis publication API. Mirrors
 * {@link SequenceMetadataService} but targets the
 * `api/post-analysis-publications/` router and its custom actions.
 */
@Injectable({ providedIn: 'root' })
export class PostAnalysisService {
  private readonly apiService = inject(ApiService);
  private readonly root = 'api/post-analysis-publications';

  /** List a file's publications (PUBLISHED for everyone, DRAFT for editors). */
  public getPublications(fileId: number) {
    const url = this.apiService.buildFullURL(this.root, `?file=${fileId}`);
    return this.apiService.get<IPostAnalysisListResponse>(url, { withCredentials: true });
  }

  /** Open (or fetch the existing) DRAFT publication for a PRIMARY file. */
  public openDraft(fileId: number) {
    const url = this.apiService.buildFullURL(this.root, `open-draft/`);
    return this.apiService.post<IPostAnalysisPublication>(url, { file: fileId }, { withCredentials: true });
  }

  /** Replace the draft publication's tags with the submitted set (scoped to the draft). */
  public saveDraftTags(publicationId: number, tags: ISequenceMetadataCreate) {
    const url = this.apiService.buildFullURL(this.root, `${publicationId}/tags/`);
    return this.apiService.post<IPostAnalysisPublication>(url, { tags }, { withCredentials: true });
  }

  /** Soft-remove a single live tag from a publication (draft or published). */
  public removeTag(
    publicationId: number,
    ref: { metadata_tag_id?: number; key?: string; value?: string; source_type?: number | null }
  ) {
    const url = this.apiService.buildFullURL(this.root, `${publicationId}/remove-tag/`);
    return this.apiService.post<IPostAnalysisPublication>(url, ref, { withCredentials: true });
  }

  /** Preview POST_ANALYSIS validation without publishing. */
  public validate(publicationId: number, acknowledgedWarningIds: string[] = []) {
    const url = this.apiService.buildFullURL(this.root, `${publicationId}/validate/`);
    return this.apiService.post<Record<string, unknown>>(
      url,
      { acknowledged_warning_ids: acknowledgedWarningIds },
      { withCredentials: true }
    );
  }

  /** Publish a draft set. Errors/warnings come back in the set-primary shape. */
  public publish(publicationId: number, acknowledgedWarningIds: string[] = []) {
    const url = this.apiService.buildFullURL(this.root, `${publicationId}/publish/`);
    return this.apiService.post<{ message: string; publication: IPostAnalysisPublication }>(
      url,
      { acknowledged_warning_ids: acknowledgedWarningIds },
      { withCredentials: true }
    );
  }
}
